<template>
  <v-row align="end">
    <v-col cols="12" md="6">
      <span class="kicker">Forecast</span>
      <h2 class="mt-1">
        {{ countryName }}
        <span v-show="!table && variable"> — {{ variable }}</span>
      </h2>
      <div class="mt-2 lx-status">
        <span class="lx-tag lx-tag--obs">Observed to {{ observedTo }}</span>
        <span class="lx-tag lx-tag--fcst">Horizon {{ horizon }}</span>
        <span class="lx-tag">{{ endogenous ? "SPX endogenous" : "SPX exogenous" }}</span>
        <span class="lx-tag" :class="{ 'lx-tag--fcst': isAlternative }">{{ scenarioLabel }}</span>
        <span v-if="scenarioHash" class="lx-badge lx-badge--hash" :title="hashTitle">
          <span class="lx-badge__k">Scenario</span>
          <span class="lx-badge__v">{{ scenarioHash }}</span>
          <button type="button" class="lx-copy" :aria-label="'Copy scenario hash ' + scenarioHash" @click="copyHash">{{ copied ? "copied" : "copy" }}</button>
        </span>
      </div>
    </v-col>
    <v-col cols="12" md="6">
      <div class="lx-toolbar justify-end">
        <template v-if="intervalsSupported">
          <label class="lx-ctl lx-check" :title="intervalsAvailable ? '' : 'No prediction intervals were provided for this result'">
            <input type="checkbox" :checked="showBands" :disabled="!intervalsAvailable" @change="$emit('update:showBands', $event.target.checked)" />
            Intervals
          </label>
          <label class="lx-ctl">
            <span class="lx-sr-only">Interval level</span>
            <select class="form-control minimal" :value="level" :disabled="!intervalsAvailable || !showBands" aria-label="Interval level" @change="$emit('update:level', Number($event.target.value))">
              <option v-for="l in levels" :key="l" :value="l">{{ l }}%</option>
            </select>
          </label>
        </template>
        <label v-show="!table" class="lx-ctl">
          <span class="lx-sr-only">Variable</span>
          <select class="form-control minimal" :value="variable" aria-label="Variable to chart" @change="$emit('update:variable', $event.target.value)">
            <option v-for="v in variables" :key="v" :value="v">{{ v }}</option>
          </select>
        </label>
        <div class="lx-seg" role="group" aria-label="View">
          <button type="button" class="lx-seg__btn" :class="{ 'lx-seg__btn--on': !table }" :aria-pressed="!table" @click="$emit('update:table', false)">Chart</button>
          <button type="button" class="lx-seg__btn" :class="{ 'lx-seg__btn--on': table }" :aria-pressed="table" @click="$emit('update:table', true)">Table</button>
        </div>
      </div>
    </v-col>
  </v-row>
</template>

<script>
export default {
  name: "ForecastResultsHeader",
  props: {
    countryName: { type: String, default: "" },
    variable: { type: String, default: "" },
    variables: { type: Array, default: () => [] },
    observedTo: { type: String, default: "" },
    horizon: { type: String, default: "" },
    endogenous: { type: Boolean, default: false },
    scenarioLabel: { type: String, default: "Scenario: Consensus" },
    scenarioHash: { type: String, default: "" },
    modelVersion: { type: String, default: "" },
    table: { type: Boolean, default: false },
    intervalsSupported: { type: Boolean, default: true },
    intervalsAvailable: { type: Boolean, default: false },
    showBands: { type: Boolean, default: true },
    level: { type: Number, default: 80 },
    levels: { type: Array, default: () => [50, 80, 95] },
  },
  emits: ["update:variable", "update:table", "update:showBands", "update:level"],
  data: () => ({ copied: false }),
  computed: {
    isAlternative() {
      return /Alternative/.test(this.scenarioLabel);
    },
    hashTitle() {
      return (
        "Deterministic hash of country, SPX mode, all input cells and model version" +
        (this.modelVersion ? ` (${this.modelVersion})` : "") +
        ". It identifies the scenario; it reproduces it only if the backend can retrieve this model version."
      );
    },
  },
  methods: {
    async copyHash() {
      try {
        await navigator.clipboard.writeText(this.scenarioHash);
        this.copied = true;
        setTimeout(() => (this.copied = false), 1500);
      } catch (e) {
        /* clipboard unavailable: the hash stays visible to select manually */
      }
    },
  },
};
</script>
